/**
 * The town is a grid of plots. Every district is one file that fills one
 * plot, written in its own local coordinates (0..PLOT_W × 0..PLOT_D); this
 * module stitches them into a single map:
 *
 *   - streets (ROAD tiles wide) run between every pair of neighbouring plots,
 *   - where a street crosses water it becomes a bridge, and where water runs
 *     off the map edge the street simply ends at the shore,
 *   - the margin around the grid continues each edge plot's water (sea) or
 *     becomes wild forest,
 *   - lamps line the streets, scatter rules fill each district's empty
 *     ground, and buildings with `enter` get their doors wired up.
 *
 * Growing the world = writing a district and adding it to `PLOTS` in
 * `./index.ts`. Nothing else needs to know.
 */
import { doorOf, footprint, GROUNDS, hash01, KINDS, WATER, type Ambience, type AmbienceKind, type Critter, type Ground, type Npc, type Place, type Portal, type Prop, type RoomId, type Spot } from '../defs.ts'

export const PLOT_W = 36
export const PLOT_D = 32
export const ROAD = 3
export const MARGIN = 3

const G = (g: Ground) => GROUNDS.indexOf(g)

export interface ScatterRule {
  /** 0..1 chance per free tile. */
  density: number
  /** What to put there, from a stable 0..1 roll and the local tile. Null skips. */
  pick: (roll: number, x: number, y: number) => Prop | null
  /** Only on these grounds (default: the natural ones). */
  on?: Ground[]
  /** Local rectangle to limit the rule to. */
  rect?: [number, number, number, number]
  /** Also on reserved tiles (paths, plazas): for flat decals like cracks and puddles. */
  anywhere?: boolean
}

/** The drawing API a district gets: local coordinates in, world coordinates out. */
export interface Plot {
  readonly w: number
  readonly d: number
  ground(g: Ground): void
  zone(x: number, y: number, w: number, d: number, g: Ground): void
  prop(...p: Prop[]): void
  /** A tram shelter at (x, y); standing in front of it opens fast travel. */
  tram(x: number, y: number): void
  place(p: Place): void
  critter(c: Critter): void
  npc(n: Npc): void
  /** Keep scatter off a rectangle. */
  keep(x: number, y: number, w: number, d: number): void
  scatter(rule: ScatterRule): void
  /** What ground a local tile has right now. */
  at(x: number, y: number): Ground
}

export interface District {
  id: string
  /** Shown on the map and in the passport. */
  name: string
  ground: Ground
  /** Ambient effects over the whole plot: falling petals, fireflies at night, gulls… */
  ambience?: AmbienceKind[]
  build(p: Plot): void
}

export interface PlotSlot { col: number; row: number; district: District }

export interface World {
  w: number
  d: number
  ground: Uint8Array
  props: Prop[]
  portals: Portal[]
  critters: Critter[]
  places: Place[]
  spots: Spot[]
  npcs: Npc[]
  ambience: Ambience[]
  /** Which district owns each plot, for the minimap and scatter. */
  plots: Array<{ id: string; name: string; x: number; y: number; w: number; d: number }>
}

/** Where scatter may grow by default: never on paths (dirt, gravel, stone…) or water. */
const NATURAL: Ground[] = ['grass', 'forest', 'moss', 'sand']

export function compose(slots: PlotSlot[], interiorNames: Partial<Record<RoomId, string>>): World {
  const cols = slots.map((s) => s.col), rows = slots.map((s) => s.row)
  const c0 = Math.min(...cols), r0 = Math.min(...rows)
  const nc = Math.max(...cols) - c0 + 1, nr = Math.max(...rows) - r0 + 1
  const W = MARGIN * 2 + nc * PLOT_W + (nc - 1) * ROAD
  const D = MARGIN * 2 + nr * PLOT_D + (nr - 1) * ROAD
  const ground = new Uint8Array(W * D).fill(G('forest'))
  const props: Prop[] = []
  const critters: Critter[] = []
  const places: Place[] = []
  const spots: Spot[] = []
  const npcs: Npc[] = []
  const reserved = new Uint8Array(W * D)
  const rules: Array<{ rule: ScatterRule; ox: number; oy: number }> = []
  const origin = (col: number, row: number) => ({ x: MARGIN + (col - c0) * (PLOT_W + ROAD), y: MARGIN + (row - r0) * (PLOT_D + ROAD) })
  const inMap = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < D
  const paint = (x: number, y: number, w: number, d: number, g: Ground) => {
    for (let i = Math.max(0, x); i < Math.min(W, x + w); i++) for (let j = Math.max(0, y); j < Math.min(D, y + d); j++) ground[j * W + i] = G(g)
  }
  const keep = (x: number, y: number, w: number, d: number) => {
    for (let i = Math.max(0, x); i < Math.min(W, x + w); i++) for (let j = Math.max(0, y); j < Math.min(D, y + d); j++) reserved[j * W + i] = 1
  }
  const groundAt = (x: number, y: number): Ground => (inMap(x, y) ? GROUNDS[ground[y * W + x]!]! : 'forest')
  const occupied = new Uint8Array(W * D)
  const addProp = (p: Prop) => {
    props.push(p)
    const f = footprint(p)
    keep(Math.floor(p.x) - 1, Math.floor(p.y) - 1, Math.ceil(f.w) + 2, Math.ceil(f.d) + 2)
    if (p.kind === 'building') keep(Math.floor(p.x) - 2, Math.floor(p.y) - 2, f.w + 4, f.d + 4)
    if (KINDS[p.kind].solid || KINDS[p.kind].seat) for (let i = Math.floor(p.x); i < Math.ceil(p.x + f.w); i++) for (let j = Math.floor(p.y); j < Math.ceil(p.y + f.d); j++) if (inMap(i, j)) occupied[j * W + i] = 1
  }

  const plots: World['plots'] = []
  const ambience: Ambience[] = []
  for (const s of slots) {
    const o = origin(s.col, s.row)
    for (const kind of s.district.ambience ?? []) ambience.push({ kind, x: o.x, y: o.y, w: PLOT_W, d: PLOT_D })
    plots.push({ id: s.district.id, name: s.district.name, x: o.x, y: o.y, w: PLOT_W, d: PLOT_D })
    paint(o.x, o.y, PLOT_W, PLOT_D, s.district.ground)
    const shift = <T extends { x: number; y: number }>(v: T): T => ({ ...v, x: v.x + o.x, y: v.y + o.y })
    const plot: Plot = {
      w: PLOT_W,
      d: PLOT_D,
      ground: (g) => paint(o.x, o.y, PLOT_W, PLOT_D, g),
      zone: (x, y, w, d, g) => {
        paint(o.x + x, o.y + y, w, d, g)
        if (!NATURAL.includes(g)) keep(o.x + x - (WATER.has(g) ? 0 : 1), o.y + y - (WATER.has(g) ? 0 : 1), w + (WATER.has(g) ? 0 : 2), d + (WATER.has(g) ? 0 : 2))
      },
      prop: (...ps) => { for (const p of ps) addProp(shift(p)) },
      tram: (x, y) => {
        addProp(shift({ kind: 'tramstop', x, y }))
        spots.push(shift({ x, y: y + 1, action: 'tram' as const }), shift({ x: x + 1, y: y + 1, action: 'tram' as const }))
        keep(o.x + x - 1, o.y + y + 1, 4, 2)
      },
      place: (p) => { places.push(shift(p)); keep(o.x + p.x - 1, o.y + p.y - 1, 3, 3) },
      critter: (c) => critters.push({ ...c, loop: c.loop.map(([x, y]) => [x + o.x, y + o.y] as [number, number]) }),
      npc: (n) => {
        npcs.push({ ...n, stops: n.stops.map(shift) })
        for (const st of n.stops) keep(o.x + st.x - 1, o.y + st.y - 1, 3, 3)
      },
      keep: (x, y, w, d) => keep(o.x + x, o.y + y, w, d),
      scatter: (rule) => rules.push({ rule, ox: o.x, oy: o.y }),
      at: (x, y) => groundAt(o.x + x, o.y + y),
    }
    s.district.build(plot)
  }

  // ─── Streets between neighbouring plots ─────────────────────────────────
  const has = new Set(slots.map((s) => `${s.col},${s.row}`))
  const isRoad = new Uint8Array(W * D)
  const strip = (x: number, y: number, w: number, d: number, along: 'x' | 'y') => {
    for (let i = x; i < x + w; i++) for (let j = y; j < y + d; j++) if (inMap(i, j)) isRoad[j * W + i] = along === 'x' ? 1 : 2
  }
  for (const s of slots) {
    const o = origin(s.col, s.row)
    const east = has.has(`${s.col + 1},${s.row}`), south = has.has(`${s.col},${s.row + 1}`)
    const corner = east && south && has.has(`${s.col + 1},${s.row + 1}`)
    // The crossing square belongs to both streets when all four plots around it exist.
    if (east) strip(o.x + PLOT_W, o.y, ROAD, PLOT_D + (corner ? ROAD : 0), 'y')
    if (south) strip(o.x, o.y + PLOT_D, PLOT_W + (corner ? ROAD : 0), ROAD, 'x')
  }
  // Each road tile: water on both sides across the street means a crossing.
  // A crossing run bounded by dry road at both ends is a bridge; one that runs
  // off the map is where the street meets the sea, and stays water.
  const wetAcross = (i: number, j: number) => {
    const t = isRoad[j * W + i]
    if (t === 1) { // street runs along x: look up and down past it
      let a = j, b = j
      while (a > 0 && isRoad[(a - 1) * W + i]) a--
      while (b < D - 1 && isRoad[(b + 1) * W + i]) b++
      return WATER.has(groundAt(i, a - 1)) && WATER.has(groundAt(i, b + 1))
    }
    let a = i, b = i
    while (a > 0 && isRoad[j * W + a - 1]) a--
    while (b < W - 1 && isRoad[j * W + b + 1]) b++
    return WATER.has(groundAt(a - 1, j)) && WATER.has(groundAt(b + 1, j))
  }
  const roadGround: Ground = 'stone'
  for (let j = 0; j < D; j++) for (let i = 0; i < W; i++) {
    if (!isRoad[j * W + i]) continue
    ground[j * W + i] = G(roadGround)
  }
  for (let j = 0; j < D; j++) for (let i = 0; i < W; i++) {
    if (!isRoad[j * W + i] || !wetAcross(i, j)) continue
    // Walk along the street direction to see whether dry road closes the run on both ends.
    const t = isRoad[j * W + i]
    const step = (k: number) => (t === 1 ? [i + k, j] : [i, j + k]) as [number, number]
    let k = 1, closedA = false, closedB = false
    for (; ; k++) { const [a, b] = step(-k); if (!inMap(a, b) || !isRoad[b * W + a]) break; if (!wetAcross(a, b)) { closedA = true; break } }
    for (k = 1; ; k++) { const [a, b] = step(k); if (!inMap(a, b) || !isRoad[b * W + a]) break; if (!wetAcross(a, b)) { closedB = true; break } }
    ground[j * W + i] = G(closedA && closedB ? 'bridge' : 'sea')
  }
  keepMask(isRoad, keep, W, D)

  // ─── Margin: continue the sea, otherwise wild forest ─────────────────────
  const gx0 = MARGIN, gy0 = MARGIN, gx1 = W - MARGIN, gy1 = D - MARGIN
  for (let j = 0; j < D; j++) for (let i = 0; i < W; i++) {
    if (i >= gx0 && i < gx1 && j >= gy0 && j < gy1) continue
    const ci = Math.min(gx1 - 1, Math.max(gx0, i)), cj = Math.min(gy1 - 1, Math.max(gy0, j))
    const edge = groundAt(ci, cj)
    if (WATER.has(edge)) ground[j * W + i] = G('sea')
    else if (edge === 'sand' || edge === 'wetsand') ground[j * W + i] = G('sand')
  }

  // ─── Street lamps every so often, on the street's edge ───────────────────
  for (const s of slots) {
    const o = origin(s.col, s.row)
    if (has.has(`${s.col + 1},${s.row}`)) for (let y = o.y + 4; y < o.y + PLOT_D - 2; y += 9) lamp(o.x + PLOT_W, y)
    if (has.has(`${s.col},${s.row + 1}`)) for (let x = o.x + 4; x < o.x + PLOT_W - 2; x += 9) lamp(x, o.y + PLOT_D)
  }
  function lamp(x: number, y: number) { furnish(x, y, { kind: 'lamppost', x, y }) }
  function furnish(x: number, y: number, p: Prop) {
    const f = footprint(p)
    for (let i = x; i < x + f.w; i++) for (let j = y; j < y + f.d; j++) if (groundAt(i, j) !== roadGround || occupied[j * W + i]) return
    props.push(p)
    for (let i = x; i < x + f.w; i++) for (let j = y; j < y + f.d; j++) occupied[j * W + i] = 1
  }
  // Between the lamps, on the street's outer edge: benches, bins and potted trees.
  for (const s of slots) {
    const o = origin(s.col, s.row)
    if (has.has(`${s.col + 1},${s.row}`)) {
      const x = o.x + PLOT_W + ROAD - 1
      furnish(x, o.y + 8, { kind: 'bench', x, y: o.y + 8, rot: 1, face: -1 })
      furnish(x, o.y + 17, { kind: 'pot', x, y: o.y + 17, variant: 'tree' })
      furnish(x, o.y + 26, { kind: 'bin', x, y: o.y + 26 })
    }
    if (has.has(`${s.col},${s.row + 1}`)) {
      const y = o.y + PLOT_D + ROAD - 1
      furnish(o.x + 8, y, { kind: 'bench', x: o.x + 8, y, face: 1 })
      furnish(o.x + 17, y, { kind: 'pot', x: o.x + 17, y, variant: 'tree' })
      furnish(o.x + 27, y, { kind: 'bin', x: o.x + 27, y })
    }
  }
  // Doorsteps: a pot of flowers either side of every front door that opens onto open ground.
  const OPEN_GROUND: Ground[] = ['grass', 'moss', 'dirt', 'forest']
  for (const b of [...props]) {
    if (b.kind !== 'building' || b.style === 'tower' || b.style === 'lighthouse' || b.style === 'pagoda') continue
    const dr = doorOf(b)
    const sides: Array<[number, number]> = b.rot ? [[dr.x, dr.y - 1], [dr.x, dr.y + 1]] : [[dr.x - 1, dr.y], [dr.x + 1, dr.y]]
    for (const [x, y] of sides) {
      if (!inMap(x, y) || occupied[y * W + x] || !OPEN_GROUND.includes(groundAt(x, y))) continue
      const p: Prop = b.style === 'shack' ? { kind: hash01(x, y, 4) < 0.5 ? 'barrel' : 'crate', x, y } : { kind: 'pot', x, y, variant: 'flower' }
      props.push(p)
      occupied[y * W + x] = 1
    }
  }

  // ─── Scatter ─────────────────────────────────────────────────────────────
  for (const { rule, ox, oy } of rules) {
    const [rx, ry, rw, rd] = rule.rect ?? [0, 0, PLOT_W, PLOT_D]
    const on = rule.on ?? NATURAL
    for (let x = rx; x < rx + rw; x++) for (let y = ry; y < ry + rd; y++) {
      const gx = ox + x, gy = oy + y
      if (!inMap(gx, gy) || (!rule.anywhere && reserved[gy * W + gx]) || occupied[gy * W + gx]) continue
      if (!on.includes(groundAt(gx, gy))) continue
      if (hash01(gx, gy, 1) >= rule.density) continue
      const p = rule.pick(hash01(gx, gy, 2), x, y)
      if (!p) continue
      props.push({ ...p, x: gx, y: gy })
      if (!KINDS[p.kind].flat) occupied[gy * W + gx] = 1
    }
  }
  // The wild margin: dense woods.
  for (let j = 0; j < D; j++) for (let i = 0; i < W; i++) {
    if (i >= gx0 && i < gx1 && j >= gy0 && j < gy1) continue
    if (groundAt(i, j) !== 'forest' || hash01(i, j, 5) > 0.55) continue
    props.push({ kind: hash01(i, j, 6) < 0.6 ? 'pine' : 'tree', x: i, y: j })
  }

  // ─── Doors into interiors ────────────────────────────────────────────────
  const portals: Portal[] = []
  for (const b of props) {
    if (b.kind !== 'building' || !b.enter) continue
    const dr = doorOf(b)
    portals.push({ x: dr.x, y: dr.y, to: b.enter, out: b.rot ? [dr.x + 1, dr.y] : [dr.x, dr.y + 1], label: interiorNames[b.enter] ?? b.enter })
  }
  return { w: W, d: D, ground, props, portals, critters, places, spots, npcs, plots, ambience }
}

function keepMask(mask: Uint8Array, keep: (x: number, y: number, w: number, d: number) => void, W: number, D: number) {
  for (let j = 0; j < D; j++) for (let i = 0; i < W; i++) if (mask[j * W + i]) keep(i, j, 1, 1)
}
